import { useEffect, useRef, useState } from "react";
import { ArrowLeftRight, ChevronUp, Loader2 } from "lucide-react";
import { PRAZO_PADRAO } from "@bookdental/schemas";
import { useAbrirNaOrganizacao } from "@/lib/use-abrir-na-organizacao";
import { PainelDaOrganizacao } from "../painel/PainelDaOrganizacao";
import { PAGINA_DO_PAINEL, type PainelChave } from "../painel/painel-tipos";
import { IndicadoresDoMes } from "./IndicadoresDoMes";
import { usePaineisDisponiveis } from "./AgirNaOrganizacao";
import { BotoesDeAgir } from "./BotoesDeAgir";
import type { OrgDoPainel } from "./painel";

type Props = { org: OrgDoPainel; ativa: boolean; trocando: boolean; onTrocar: () => void; onRecolher: () => void };

/**
 * O que abre embaixo da organização clicada: o mês comparado ao anterior e, nas abas de "Agir sem
 * trocar", o painel da área escolhida ali mesmo — agindo nela sem trocar a ativa. As vizinhas
 * continuam na lista; o "Recolher" do pé evita subir até a linha pra fechar.
 */
export function DetalheDaLinha({ org, ativa, trocando, onTrocar, onRecolher }: Props) {
  const { preview, paineis } = usePaineisDisponiveis(org.id);
  const { abrir, abrindo } = useAbrirNaOrganizacao();
  const [painel, setPainel] = useState<PainelChave | null>(null);
  const area = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (painel) area.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [painel]);

  return (
    <div className="space-y-4 border-t border-zinc-100 bg-zinc-50/60 px-4 py-4 dark:border-border dark:bg-muted/20">
      <div className="space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">Este mês</p>
        <IndicadoresDoMes organizationId={org.id} />
      </div>
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">Agir sem trocar</p>
          {!ativa && (
            <button
              type="button"
              onClick={onTrocar}
              disabled={trocando}
              className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-[13px] font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-60 dark:border-border dark:bg-card dark:text-foreground"
            >
              {trocando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowLeftRight className="h-3.5 w-3.5" />}
              Trocar para {org.nome}
            </button>
          )}
        </div>
        <BotoesDeAgir paineis={paineis} org={org} preview={preview} aberto={painel} onEscolher={(p) => setPainel((atual) => (atual === p ? null : p))} />
      </div>
      {painel && preview && (
        <div ref={area} className="scroll-mt-20 space-y-2">
          <PainelDaOrganizacao
            painel={painel}
            organizationId={org.id}
            organizationName={org.nome}
            prazo={preview.prazo ?? PRAZO_PADRAO}
            abrindo={abrindo}
            onAbrirNaOrganizacao={() => abrir(org.id, org.nome, PAGINA_DO_PAINEL[painel])}
          />
          <div className="flex justify-center">
            <button
              type="button"
              onClick={onRecolher}
              className="inline-flex h-8 items-center gap-1 rounded-md px-3 text-[12.5px] font-medium text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800 dark:text-muted-foreground dark:hover:bg-muted"
            >
              <ChevronUp className="h-4 w-4" />
              Recolher {org.nome}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
